import React, { useEffect, useState } from "react";
import Modal from "../../CommonComponents/TransactionModal";
import { toast } from "react-toastify";
import { createExpense, fetchExpenses } from "../../../services/expenseApi";
import { fetchBranches } from "../../../services/branchApi";
import { authStorage } from "../../../utils/authStorage";
import { fetchUsers } from "../../../services/usersApi";
import Select from "react-select";

const formatLocalDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function ExpensesPage() {
  const getMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: formatLocalDate(start), endDate: formatLocalDate(end) };
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState(() => getMonthRange());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [adminFilters, setAdminFilters] = useState({ branch_id: "", user_id: "" });
  const [form, setForm] = useState({
    date: "",
    category: "",
    categoryOther: "",
    description: "",
    amount: "",
    paymentMethod: "cash",
    receiptNo: "",
    notes: "",
    branch_id: "",
  });
  const [errors, setErrors] = useState({});
  const [detailsExpense, setDetailsExpense] = useState(null);
  const [filterError, setFilterError] = useState("");
  const [branches, setBranches] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const isAdmin = authStorage.getUser()?.user_type === 1;
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 40,
      borderColor: state.isFocused ? "var(--color-primary)" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 1px var(--color-primary)" : "none",
      borderRadius: 8,
      "&:hover": { borderColor: "var(--color-primary)" },
      fontSize: 14,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 8,
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      zIndex: 30,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "rgba(0,0,0,0.04)"
          : "white",
      color: state.isSelected ? "#fff" : "#111827",
      fontSize: 14,
    }),
    singleValue: (base) => ({
      ...base,
      color: "#111827",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),
  };
  const branchOptions = [
    { value: "", label: "All Branches" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const modalBranchOptions = [
    { value: "", label: "Select branch" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const categoryOptions = [
    { value: "", label: "Select category" },
    { value: "Supplies", label: "Supplies" },
    { value: "Transport", label: "Transport" },
    { value: "Others", label: "Others" },
  ];
  const paymentMethodOptions = [
    { value: "cash", label: "Cash" },
    { value: "gcash", label: "GCash" },
  ];
  const filteredCashiers = adminFilters.branch_id
    ? cashiers.filter(
      (u) => String(u.branch_id ?? u.branch?.id ?? "") === String(adminFilters.branch_id)
    )
    : cashiers;
  const cashierOptions = [
    { value: "", label: "All Cashiers / Admin" },
    ...filteredCashiers.map((u) => ({ value: String(u.id), label: u.username || u.email })),
  ];

  useEffect(() => {
    if (!isModalOpen) return;
    const today = formatLocalDate(new Date());
    setForm((prev) => ({ ...prev, date: today }));
  }, [isModalOpen]);

  const loadExpenses = (nextFilters = filters, nextAdminFilters = adminFilters) => {
    setIsLoading(true);
    const params = {};
    if (nextFilters.startDate) params.startDate = nextFilters.startDate;
    if (nextFilters.endDate) params.endDate = nextFilters.endDate;
    if (isAdmin && nextAdminFilters.branch_id) params.branch_id = nextAdminFilters.branch_id;
    if (isAdmin && nextAdminFilters.user_id) params.user_id = nextAdminFilters.user_id;
    if ((params.startDate && !params.endDate) || (!params.startDate && params.endDate)) {
      setFilterError("Please select both From and To dates.");
      setIsLoading(false);
      return;
    }
    if (params.startDate && params.endDate && params.startDate > params.endDate) {
      setFilterError("From date cannot be later than To date.");
      setIsLoading(false);
      return;
    }
    setFilterError("");
    if (!params.startDate && !params.endDate) {
      setExpenses([]);
      setIsLoading(false);
      return;
    }
    fetchExpenses(params)
      .then((res) => {
        setExpenses(res.data || []);
        setCurrentPage(1);
      })
      .catch((error) => {
        const msg = error.response?.data?.message || "Failed to fetch expenses";
        toast.error(msg);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const handleAdminFilterChange = (key, value) => {
    const nextAdminFilters = { ...adminFilters, [key]: value };
    if (key === "branch_id" && value && nextAdminFilters.user_id) {
      const selectedUser = cashiers.find((u) => String(u.id) === String(nextAdminFilters.user_id));
      const isSelectedUserInBranch =
        selectedUser &&
        String(selectedUser.branch_id ?? selectedUser.branch?.id ?? "") === String(value);
      if (!isSelectedUserInBranch) {
        nextAdminFilters.user_id = "";
      }
    }
    setAdminFilters(nextAdminFilters);
    loadExpenses(filters, nextAdminFilters);
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchBranches()
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setBranches(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        toast.error("Failed to load branches");
      });
    fetchUsers()
      .then((res) => {
        const data = res.data?.data ?? res.data ?? [];
        setCashiers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        toast.error("Failed to load users");
      });
  }, [isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name, option) => {
    const value = option?.value || "";
    setForm((prev) => {
      if (name === "category" && value !== "Others") {
        return { ...prev, [name]: value, categoryOther: "" };
      }
      return { ...prev, [name]: value };
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "category" && errors.categoryOther) {
      setErrors((prev) => ({ ...prev, categoryOther: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.date) nextErrors.date = "Date is required.";
    if (!form.category) nextErrors.category = "Category is required.";
    if (form.category === "Others" && !form.categoryOther.trim()) {
      nextErrors.categoryOther = "Please specify the category.";
    }
    if (!form.description.trim()) nextErrors.description = "Description is required.";
    if (form.amount === "" || Number(form.amount) <= 0) {
      nextErrors.amount = "Amount must be greater than 0.";
    }
    if (isAdmin && !form.branch_id) {
      nextErrors.branch_id = "Branch is required.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    createExpense({
      date: form.date,
      category: form.category,
      categoryOther: form.categoryOther,
      description: form.description,
      amount: form.amount,
      paymentMethod: form.paymentMethod,
      receiptNo: form.receiptNo,
      notes: form.notes,
      branch_id: isAdmin ? form.branch_id : undefined,
    })
      .then(() => {
        toast.success("Expense saved");
        setIsModalOpen(false);
        setForm((prev) => ({
          ...prev,
          category: "",
          categoryOther: "",
          description: "",
          amount: "",
          paymentMethod: "cash",
          receiptNo: "",
          notes: "",
          branch_id: "",
        }));
        loadExpenses();
      })
      .catch((error) => {
        const msg = error.response?.data?.message || "Failed to save expense";
        toast.error(msg);
      });
  };

  const totalPages = Math.max(1, Math.ceil(expenses.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const pagedExpenses = expenses.slice(startIndex, startIndex + itemsPerPage);
  const tableColCount = isAdmin ? 8 : 7;
  const totalSelectedAmount = expenses.reduce(
    (sum, exp) => sum + Number(exp.amount || 0),
    0
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Expenses</h1>
        <button
          type="button"
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium shadow-sm hover:opacity-90 transition"
          onClick={() => setIsModalOpen(true)}
        >
          Add Expense
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl py-6 px-4 md:px-5 shadow-sm">
        <div className="text-xs text-gray-500 mb-2">
          Default view shows expenses for the current month.
        </div>
        <div className="mb-4 flex flex-col gap-3">
          <div className="w-full">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 w-full">
              <div className="text-xs text-gray-400 mb-2">Filter by date range</div>
              <div className="flex flex-col lg:flex-row lg:items-center lg:flex-nowrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-500 w-10">From</div>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, startDate: e.target.value }));
                      if (filterError) setFilterError("");
                    }}
                    className="bg-white text-sm outline-none border border-gray-200 rounded-md px-2 py-1.5 w-full lg:w-auto"
                  />
                </div>
                <span className="text-gray-300 hidden lg:block">|</span>
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-500 w-10">To</div>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, endDate: e.target.value }));
                      if (filterError) setFilterError("");
                    }}
                    className="bg-white text-sm outline-none border border-gray-200 rounded-md px-2 py-1.5 w-full lg:w-auto"
                  />
                </div>
                <div className="hidden lg:flex items-center gap-2 ml-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg border text-sm"
                    onClick={() => {
                      setFilters({ startDate: "", endDate: "" });
                      setFilterError("");
                      setExpenses([]);
                      setCurrentPage(1);
                      setAdminFilters({ branch_id: "", user_id: "" });
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm"
                    onClick={() => {
                      if (isAdmin) {
                        const resetAdmin = { branch_id: "", user_id: "" };
                        setAdminFilters(resetAdmin);
                        loadExpenses(filters, resetAdmin);
                        return;
                      }
                      loadExpenses();
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-3 lg:hidden">
              <button
                type="button"
                className="px-3 py-2 rounded-lg border text-sm"
                onClick={() => {
                  setFilters({ startDate: "", endDate: "" });
                  setFilterError("");
                  setExpenses([]);
                  setCurrentPage(1);
                  setAdminFilters({ branch_id: "", user_id: "" });
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm"
                onClick={() => {
                  if (isAdmin) {
                    const resetAdmin = { branch_id: "", user_id: "" };
                    setAdminFilters(resetAdmin);
                    loadExpenses(filters, resetAdmin);
                    return;
                  }
                  loadExpenses();
                }}
              >
                Apply
              </button>
            </div>
            {filterError && (
              <div className="text-xs text-red-500 mt-2">{filterError}</div>
            )}
            {isAdmin && (
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-3 w-full">
                <div className="text-xs text-gray-400 mb-2">Filter by branch or cashier</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Branch</div>
                    <Select
                      options={branchOptions}
                      value={branchOptions.find((o) => o.value === adminFilters.branch_id) || branchOptions[0]}
                      onChange={(opt) => handleAdminFilterChange("branch_id", opt?.value || "")}
                      classNamePrefix="react-select"
                      isSearchable={false}
                      styles={selectStyles}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Cashier / Admin</div>
                    <Select
                      options={cashierOptions}
                      value={cashierOptions.find((o) => o.value === adminFilters.user_id) || cashierOptions[0]}
                      onChange={(opt) => handleAdminFilterChange("user_id", opt?.value || "")}
                      classNamePrefix="react-select"
                      isSearchable={false}
                      styles={selectStyles}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 whitespace-nowrap">Date</th>
                <th className="py-2 whitespace-nowrap">Category</th>
                <th className="py-2 w-[30%]">Description</th>
                <th className="py-2 whitespace-nowrap">Amount</th>
                <th className="py-2 whitespace-nowrap">Payment</th>
                <th className="py-2">Branch</th>
                {isAdmin && <th className="py-2 whitespace-nowrap">Cashier</th>}
                <th className="py-2 whitespace-nowrap">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={tableColCount} className="py-4 text-center text-gray-500">
                    Loading expenses...
                  </td>
                </tr>
              )}
              {!isLoading && expenses.length === 0 && (
                <tr>
                  <td colSpan={tableColCount} className="py-4 text-center text-gray-500">
                    No expenses found.
                  </td>
                </tr>
              )}
              {!isLoading && pagedExpenses.map((exp) => (
                <tr key={exp.id} className="border-b last:border-b-0">
                  <td className="py-2 whitespace-nowrap">{exp.date}</td>
                  <td className="py-2 whitespace-nowrap">{exp.category_other || exp.category}</td>
                  <td className="py-2 max-w-0">
                    <div className="w-full truncate" title={exp.description || ""}>
                      {exp.description}
                    </div>
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    ₱ {Number(exp.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="py-2 whitespace-nowrap">{exp.payment_method}</td>
                  <td className="py-2 whitespace-nowrap">{exp.branch?.name || (exp.branch_id ? `Branch #${exp.branch_id}` : "-")}</td>
                  {isAdmin && (
                    <td className="py-2 whitespace-nowrap">
                      {exp.user?.username || exp.user?.email || (exp.user_id ? `User #${exp.user_id}` : "-")}
                    </td>
                  )}
                  <td className="py-2">
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => setDetailsExpense(exp)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {!isLoading && expenses.length > 0 && (
              <tfoot>
                <tr className="border-t">
                  <td colSpan={3} className="py-2 text-right text-sm font-medium text-gray-700">
                    Total Expenses:
                  </td>
                  <td className="py-2 text-sm font-semibold text-gray-800">
                    ₱ {totalSelectedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td colSpan={tableColCount - 4} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {expenses.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4">
            <div className="text-xs text-gray-500">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, expenses.length)} of {expenses.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <div className="text-xs text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal open={!!detailsExpense} onClose={() => setDetailsExpense(null)}>
        {detailsExpense && (
          <div className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Expense Details</h2>
              <p className="text-xs text-gray-500">Full expense information</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500 text-xs">Date</div>
                <div className="text-gray-800">{detailsExpense.date || "—"}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Category</div>
                <div className="text-gray-800">{detailsExpense.category_other || detailsExpense.category || "—"}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-gray-500 text-xs">Description</div>
                <p className="text-gray-800 whitespace-pre-wrap break-words leading-relaxed max-w-prose">
                  {detailsExpense.description || "—"}
                </p>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Amount</div>
                <div className="text-gray-800">₱ {Number(detailsExpense.amount || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Payment Method</div>
                <div className="text-gray-800">{detailsExpense.payment_method || "—"}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Branch</div>
                <div className="text-gray-800">
                  {detailsExpense.branch?.name || (detailsExpense.branch_id ? `Branch #${detailsExpense.branch_id}` : "—")}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Cashier / Admin</div>
                <div className="text-gray-800">
                  {detailsExpense.user?.username || detailsExpense.user?.email || (detailsExpense.user_id ? `User #${detailsExpense.user_id}` : "—")}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Receipt No.</div>
                <div className="text-gray-800">{detailsExpense.receipt_no || "—"}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-gray-500 text-xs">Notes</div>
                <div className="text-gray-800 whitespace-pre-wrap">{detailsExpense.notes || "—"}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">Created At</div>
                <div className="text-gray-800">
                  {detailsExpense.created_at
                    ? new Date(detailsExpense.created_at).toLocaleString()
                    : detailsExpense.createdAt
                      ? new Date(detailsExpense.createdAt).toLocaleString()
                      : "—"}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border text-sm"
                onClick={() => setDetailsExpense(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Add Expense</h2>
          <p className="text-xs text-gray-500">Record a cashier expense entry.</p>
          <p className="text-xs text-gray-500 mt-1">
            <span className="text-red-500 font-medium">*</span> Required fields
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && (
              <div>
                <label className="text-xs text-gray-600">
                  Branch <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Select
                    options={modalBranchOptions}
                    value={modalBranchOptions.find((o) => o.value === form.branch_id) || modalBranchOptions[0]}
                    onChange={(opt) => handleSelectChange("branch_id", opt)}
                    classNamePrefix="react-select"
                    isSearchable={false}
                    styles={selectStyles}
                  />
                </div>
                {errors.branch_id && (
                  <p className="text-xs text-red-500 mt-1">{errors.branch_id}</p>
                )}
              </div>
            )}
            <div>
              <label className="text-xs text-gray-600">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
              {errors.date && (
                <p className="text-xs text-red-500 mt-1">{errors.date}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-600">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find((o) => o.value === form.category) || categoryOptions[0]}
                  onChange={(opt) => handleSelectChange("category", opt)}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={selectStyles}
                />
              </div>
              {errors.category && (
                <p className="text-xs text-red-500 mt-1">{errors.category}</p>
              )}
            </div>
          </div>

          {form.category === "Others" && (
            <div>
              <label className="text-xs text-gray-600">
                Specify Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="categoryOther"
                value={form.categoryOther}
                onChange={handleChange}
                placeholder="Enter category"
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
              {errors.categoryOther && (
                <p className="text-xs text-red-500 mt-1">{errors.categoryOther}</p>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-gray-600">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="What was this expense for?"
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
            />
            {errors.description && (
              <p className="text-xs text-red-500 mt-1">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
              {errors.amount && (
                <p className="text-xs text-red-500 mt-1">{errors.amount}</p>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-600">Payment Method</label>
              <div className="mt-1">
                <Select
                  options={paymentMethodOptions}
                  value={paymentMethodOptions.find((o) => o.value === form.paymentMethod) || paymentMethodOptions[0]}
                  onChange={(opt) => handleSelectChange("paymentMethod", opt)}
                  classNamePrefix="react-select"
                  isSearchable={false}
                  styles={selectStyles}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Receipt No. (Optional)</label>
              <input
                type="text"
                name="receiptNo"
                value={form.receiptNo}
                onChange={handleChange}
                placeholder="OR # / Invoice #"
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Notes (Optional)</label>
              <input
                type="text"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Additional details"
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 rounded-lg border text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium"
            >
              Save Expense
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
